/* eslint-disable no-undefined */
import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import {TscStyles, TscThemeName, TscThemeNames, Spinner, TscButtonType, Glyphs, ControlButton, SelectInput, EmptyState} from '@techsmith/tsc-cloud-style-guide';
import cssConstants from '../../constants/cssConstants';
import Constants, {AnnotationModes, ServiceNames} from '../../constants/Constants';
import config from '../../service/config';
import Comment from './Comment';
import CommentInput from './CommentInput';
import {IComment} from '../../model/commentModel';
import CommentFilter from './CommentFilter';
import {useTranslation} from 'react-i18next';
import NotificationToggle from '../conversation/NotificationToggle';
import UserMessaging from '../../util/UserMessaging';
import {IWorkerState, SetupWorkerState} from '../../model/workerState';
import SpotSpeechBubbleGraphicGlyph from '../../static/svg/SpotSpeechBubbleGraphic.svg';
import SpotSearchGraphicGlyph from '../../static/svg/SpotSearchGraphic.svg';
import SelfIdentifyModal from '../modals/SelfIdentifyModal';
import selfIdentifyService from '../../service/selfIdentifyservice';
import {selfIdentifiedUserStore, updateSelfIdentifiedUserProviderState} from '../../context/selfIdentifiedUserProvider';
import {ButtonStyledAsAnchor} from '../util/StyledElements';
import {selectedConversationStore, updateSelectedConversationState} from '../../context/selectedConversationProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {canvasStore} from '../../context/canvasProvider';
import {commentsStore, updateCommentsState} from '../../context/commentsProvider';
import {sortCommentsByTime} from '../../util/CommentSorter';
import {conversationsStore, updateConversationsState} from '../../context/conversationsProvider';
import {IConversationData} from '../../model/conversationModel';
import CreateConversationModal from '../modals/CreateConversationModal';
import {updateAnnotationManagerState} from '../../context/annotationManagerProvider';
import LocalStorageWrapper from '../util/LocalStorageWrapper';
import AriaHelper from '../../util/AriaHelper';
import {themeStore} from '../../context/themeProvider';
import {mediaStore} from '../../context/mediaProvider';
import {mediaOperationsStore} from '../../context/mediaOperationsProvider';
import logging from '../../service/logging';
import conversationApi from '../../service/conversationApi';
import {AlertModalPortal} from '../util/ModalPortalHelper';
import {importMediaInterface} from '../../hooks/dynamicImport';
import {displayModes} from '@techsmith/techsmith-smartplayer';
import CssUtil from '../../util/CssUtil';

const ConversationPane = styled.div<{theme: TscThemeNames; isLti: boolean; conversationPaneOpen: boolean; smartPlayerDisplayMode: string; marketingPaneVisible: boolean}>`
   background-color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.medium : TscStyles.color.ui.dawn.light};
   display: flex;
   flex-direction: column;
   flex: 0 0 auto;
   width: ${props => props.smartPlayerDisplayMode === displayModes.Theater ? 'auto' : cssConstants.conversationPanelWidth};
   height: inherit;
   z-index: ${cssConstants.zIndex.conversation};
   outline: none;
   position: relative;
   overflow: auto;

   @media screen and (max-width: ${cssConstants.conversationsBreakpoint}px) {
      ${props => !props.conversationPaneOpen && 'display: none;'}
      width: 100%;
      height: auto;
      top: 0;
      right: 0;
      margin-top: 0;
      position: relative;
      overflow: visible;
      border-radius: 0;
      height: 100%;

      /* Prevent iOS mobile auto zoom on textarea focus */
      @supports (-webkit-overflow-scrolling: touch) {
         textarea {
            font-size: 1rem;
         }
      }
   }
`;

const CommentSection = styled.ol<{showEmptyState: boolean}>`
   position: relative;
   padding: 0.5rem 0 0rem 0;
   height: 100%;
   -webkit-overflow-scrolling: touch;
   list-style: none;
   margin: ${props => props.showEmptyState ? 'auto' : '0'};
   overflow: ${props => props.showEmptyState ? 'hidden' : 'visible'};
`;

const CommentSectionHeader = styled.div<{theme: TscThemeNames}>`
   display: flex;
   padding: 0.5rem 1rem;
   font-size: 1rem;
   font-weight: 600;
   align-items: center;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
   flex: 0 0 auto;
   overflow: hidden;
`;

const SectionHeading = styled.h2`
   font-size: 1rem;
   margin: 0;
`;

const CommentHeaderRight = styled.div`
   display: flex;
   flex-grow: 1;
   justify-content: flex-end;

   > * {
      margin-left: 0.5rem;
   }
`;

const SpinnerWrapper = styled.div`
   height: calc(100vh - 5.25rem);
   display: flex;
   align-items: center;
   padding: 5rem;
`;

export const EmptyStateWrapper = styled.div<{theme: TscThemeNames}>`
   display: flex;
   flex-direction: column;
   justify-content: center;
   align-items: center;
   height: 100%;
   font-size: .875rem;

   > div {
      box-sizing: border-box;
   }
`;

const SelfIdentifiedUserWrapper = styled.div<{theme: TscThemeNames}>`
   padding: 0.5rem 1rem 0;
   display: flex;
   font-weight: 400;
   word-break: break-word;
   align-items: center;
   justify-content: space-between;
   font-size: 0.875rem;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};

   > * {
      margin-left: 0.5rem;
      flex-shrink: 0;
   }
`;

const ConversationSelectWrapper = styled.div<{theme: TscThemeNames; borderNeeded: boolean}>`
   padding: 0.5rem 1rem;
   border-bottom: 1px solid ${props => !props.borderNeeded ? 'transparent' : props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.medium};

   label {
      position: absolute;
      left: -9999px;
   }

   /* Prevent iOS mobile auto zoom on textarea focus */
   @supports (-webkit-overflow-scrolling: touch) {
      select {
         font-size: 1rem;
      }
   }
`;

const NoCommentEmptyStateMessageText = styled.div`
   font-weight: ${TscStyles.font.weight.semibold};
`;

const BottomSpacer = styled.div`
   height: 1px; // Prevents grid from collapsing bottom conversation pane margin
`;

const headerBackgroundOpacity = 0.75;
const ConversationPaneHeaderWrapper = styled.div<{theme: TscThemeNames}>`
   position: sticky;
   top: 0;
   background-color: ${props => CssUtil.toRgba(props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.medium : TscStyles.color.ui.dawn.light, headerBackgroundOpacity)};
   backdrop-filter: blur(12px);
   z-index: ${cssConstants.zIndex.viewPageElement};
   box-shadow: ${props => props.theme === TscThemeName.dusk ? '0 1px 5px rgba(0,0,0,0.2)' : '0 1px 5px rgba(0,0,0,0.08)'};
   :after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 1px;
      backdrop-filter: ${props => props.theme === TscThemeName.dusk ? 'brightness(110%)' : 'brightness(200%)'};
   }
`;

export const ConversationBase: React.FC<IStateMappedProps & IConversationProps> = ({canvasRef, comments, conversations, Title, VideoReviewId, conversationPaneOpen, conversationLoadingState, setConversationLoadingState, initialSelectedComment, smartPlayerDisplayMode, theme, Hash, showSelfIdentifyModal, marketingPaneVisible, IsReplacingMedia}) => {
   const {t} = useTranslation();
   const [addCommentLoadingState, setAddCommentLoadingState] = SetupWorkerState();
   const [searchText, updateSearchText] = useState('');
   const [cachedNewComments, setCachedNewComments] = useState<IComment[]>([]);
   const [commentsToHighlight, updateCommentsToHighlight] = useState(null);
   const [showSelfIdentifyAlertModal, setShowSelfIdentifyAlertModal] = useState(false);
   const [showCreateConversationModal, setShowCreateConversationModal] = useState(false);
   const isSelfIdentified = config.user.TechSmithId !== Constants.nullTechSmithId && config.user.DisplayName && !config.user.IsSignedIn;
   const commentsRef = React.useRef<IComment[]>([]);
   commentsRef.current = comments || [];
   let intervalId = 0;

   useEffect(() => {
      config.user.TechSmithId === Constants.nullTechSmithId && selfIdentifyService.setupSelfIdentifiedUserFromLocalStorage();
   }, []);

   useEffect(() => {
      if (config.featureSwitches.UseSingleConversation && conversations?.length) {
         const singleConversation = conversations[0];
         updateSelectedConversationState({Title: singleConversation.Title, VideoReviewId: singleConversation.VideoReviewId, ConversationHash: singleConversation.ConversationHash});
      }
   }, [conversations]);

   useEffect(() => {
      VideoReviewId && startPoll();

      return () => clearInterval(intervalId);
   }, [VideoReviewId, IsReplacingMedia]);

   useEffect(() => {
      initialSelectedComment && updateCommentsToHighlight([initialSelectedComment.id]);
   }, [initialSelectedComment]);

   useEffect(() => {
      if (!searchText.length && cachedNewComments.length) {
         setCommentsToHighlight(comments, cachedNewComments);
         updateCommentsState({comments: cachedNewComments});
         setCachedNewComments([]);
      }
   }, [searchText]);

   const mapCommentsToTextArray = (commentsToMap: IComment[]): string[] => {
      if (!commentsToMap.length) {
         return [];
      }
      return commentsToMap.flatMap(c => [c.text, ...c.replies.map(cr => cr.text)]);
   };

   const startPoll = () => {
      intervalId = window.setInterval(() => {
         if (!IsReplacingMedia) {
            const asyncIntervalFunc = async () => {
               try {
                  const newComments = await conversationApi.getConversationComments(VideoReviewId); // intentional non-async

                  if (JSON.stringify(mapCommentsToTextArray(newComments)) !== JSON.stringify(mapCommentsToTextArray(commentsRef.current))) {
                     if (commentsRef.current.some(c => c.isFiltered)) {
                        setCachedNewComments(newComments);
                     } else {
                        setCommentsToHighlight(commentsRef.current, newComments);
                        updateCommentsState({comments: newComments});
                     }
                  }
               } catch (e) {
                  if (e?.status === Constants.statusCodes.notFound) {
                     window.clearInterval(intervalId);
                     logging.warn('Detected that review was deleted. Stopping comment polling loop.');
                     UserMessaging.warn(t('conversations.conversationDeleted'));
                  }
               }
            };

            void asyncIntervalFunc(); // intentional non-await-async in interval
         }
      }, Constants.conversationRefreshInterval);
   };

   const setCommentsToHighlight = (prevComments: IComment[], newComments: IComment[]) => {
      const prevCommentIds = prevComments.map(c => c.id);
      const newCommentsToHighlight = newComments?.map(c => c.id).filter(id => prevCommentIds.indexOf(id) === -1);
      updateCommentsToHighlight(newCommentsToHighlight);
   };

   const setSearchText = (text: string) => {
      updateSearchText(text);

      if (!text.length) {
         void AriaHelper.readAlert(t('conversations.commentSearchTextClearedAlert'));
         comments && updateCommentsState({comments: comments.map(c => Object.assign(c, {isFiltered: undefined, replies: c.replies.map((reply: IComment) => Object.assign({}, reply, {isFiltered: undefined}))}))});
      }
   };

   const onPerformCommentSearch = () => {
      setCommentsToHighlight(comments, []);
      if (searchText.length) {
         const commentsToDisplay = comments?.filter(comment =>
            comment.text.toLowerCase().includes(searchText.toLowerCase()) ||
            comment.user.DisplayName.toLowerCase().includes(searchText.toLowerCase()) ||
            comment.replies.filter((reply: IComment) => reply.text.toLowerCase().includes(searchText.toLowerCase()) || reply.user.DisplayName.toLowerCase().includes(searchText.toLowerCase())).length !== 0
         );
         void AriaHelper.readAlert(t('conversations.commentSearchTextAlert', {count: commentsToDisplay.length}));
         comments && updateCommentsState({
            comments: comments.map(c => Object.assign(c, {
               isFiltered: !(commentsToDisplay.indexOf(c) > -1),
               replies: c.replies.map((reply: IComment) => Object.assign({}, reply, {isFiltered: !reply.text.toLowerCase().includes(searchText.toLowerCase()) && !reply.user.DisplayName.toLowerCase().includes(searchText.toLowerCase())}))
            }))
         });
      } else {
         comments && updateCommentsState({comments: comments.map(c => Object.assign(c, {isFiltered: undefined, replies: c.replies.map((reply: IComment) => Object.assign({}, reply, {isFiltered: undefined}))}))});
      }
   };

   const submitNewComment = async (commentText: string) => {
      const mediaInterface = await importMediaInterface();
      setAddCommentLoadingState({isWorking: true, hasError: false});
      LocalStorageWrapper.removeItem(Constants.localStorageKeys.unsavedCommentText.replace('{videoReviewId}', VideoReviewId));

      try {
         if (conversations) {
            const conversationsCopy = [...conversations];
            const conversationToUpdate = conversationsCopy.find(conversation => conversation.VideoReviewId === VideoReviewId);
            const updateCommenterCount = comments.findIndex(comment => comment.authorId === config.user.TechSmithId) === -1;
            const updatedConversation = Object.assign({}, conversationToUpdate, {CommentCount: conversationToUpdate.CommentCount + 1, CommenterCount: updateCommenterCount ? conversationToUpdate.CommenterCount + 1 : conversationToUpdate.CommenterCount});
            conversationsCopy.splice(conversationsCopy.findIndex(oldConversation => oldConversation.VideoReviewId === VideoReviewId), 1, updatedConversation);
            updateConversationsState({conversations: conversationsCopy});
         }

         const commentsCopy = [...comments];
         const newComment = Object.assign({},
            await conversationApi.addComment(VideoReviewId,
               {
                  text: commentText.trim(),
                  annotation: canvasRef?.getObjects().length ? JSON.stringify(canvasRef?.toDatalessJSON()) : '',
                  isResolved: false, mediaTimeInMilliseconds: mediaInterface.currentTime * 1000,
                  originatingService: config.featureSwitches.IsScreencast ? ServiceNames.screencast : ServiceNames.knowmia
               }
            ),
            {
               authorId: config.user.TechSmithId,
               user: {
                  ProfileImageUrl: config.user.AvatarImageUrl,
                  DisplayName: config.user.DisplayName,
                  Id: config.user.TechSmithId
               }
            }
         );
         commentsCopy.push(newComment);
         updateSearchText('');
         setCommentsToHighlight(comments, [newComment]);
         const sortedComments = !searchText.length ?
            sortCommentsByTime(commentsCopy) :
            sortCommentsByTime(commentsCopy).map(c => Object.assign(c, {isFiltered: undefined, replies: c.replies.map((reply: IComment) => Object.assign({}, reply, {isFiltered: undefined}))}));
         updateCommentsState({comments: sortedComments});
         updateAnnotationManagerState({annotationMode: newComment.annotation ? AnnotationModes.display : AnnotationModes.none});
         updateSelectedConversationState({SelectedComment: newComment});
         void AriaHelper.readAlert(t('conversations.commentSubmitted'));
         setAddCommentLoadingState({isWorking: false, hasError: false});
      } catch (e) {
         logging.error('Failed to submit new comment', e);
         setAddCommentLoadingState({isWorking: false, hasError: true});
      }
   };

   const clearSelfIdentifiedUser = () => {
      try {
         setAddCommentLoadingState({isWorking: true, hasError: false});
         canvasRef?.clear();
         updateAnnotationManagerState({annotationMode: AnnotationModes.none});
         selfIdentifyService.forgetSelfIdentifiedUser();
         setShowSelfIdentifyAlertModal(false);
         setAddCommentLoadingState({isWorking: false, hasError: false});
      } catch (e) {
         logging.error('Failed to clear self identified user', e);
         setAddCommentLoadingState({isWorking: false, hasError: true});
      }
   };

   const updateConversation = (e: React.ChangeEvent<HTMLSelectElement>): void => {
      if (!e.target.value) {
         updateSelectedConversationState({VideoReviewId: '', Title: null, SelectedComment: null, ConversationHash: null});
         return;
      }

      const newSelectedConversation = conversations.find(conversation => conversation.VideoReviewId === e.target.value);
      updateSelectedConversationState({VideoReviewId: newSelectedConversation.VideoReviewId, Title: newSelectedConversation.Title, ConversationHash: newSelectedConversation.ConversationHash, SelectedComment: null});
   };

   const createConversation = async () => {
      setConversationLoadingState({isWorking: true, hasError: false});
      try {
         const newConversation = await conversationApi.createConversation(Hash, Title);
         conversations.push(newConversation);
         updateConversationsState({conversations: conversations});
         updateSelectedConversationState({Title: newConversation.Title, VideoReviewId: newConversation.VideoReviewId, ConversationHash: newConversation.ConversationHash, SelectedComment: null});
         setConversationLoadingState({isWorking: false, hasError: false});
      } catch {
         setConversationLoadingState({isWorking: false, hasError: true});
      }
   };

   const getEmptyStateBody = () => {
      if (conversationLoadingState.hasError) {
         return (
            <EmptyState
               themeName={theme}
               title={t('conversations.emptyStateText.conversationNotFound')}
               primaryButtonText={t('general.close')}
               primaryButtonType={TscButtonType.secondary}
               onPrimaryClick={() => {
                  conversationLoadingState.hasError && updateSelectedConversationState({VideoReviewId: '', Title: null, SelectedComment: null, ConversationHash: null});
                  updateConversationsState({conversationPaneOpen: false});
               }}
            />
         );
      } else if (!conversations?.length && config.watchConfig.isOwnerOrAdmin) {
         return (
            <EmptyState
               themeName={theme}
               title={t('conversations.emptyStateText.noConversationsExist')}
               text={t('conversations.emptyStateSubText.noConversationsExist')}
               primaryButtonText={config.featureSwitches.UseSingleConversation ? t('conversations.enableConversation') : t('conversations.addConversationTitle')}
               primaryButtonType={TscButtonType.secondary}
               onPrimaryClick={() => config.featureSwitches.UseSingleConversation ? createConversation() : setShowCreateConversationModal(true)}
            />
         );
      } else if (!VideoReviewId) {
         return (
            <EmptyState
               themeName={theme}
               title={t('conversations.emptyStateText.noConversationSelected')}
               text={t('conversations.emptyStateSubText.noConversationSelected')}
            />
         );
      }

      const noCommentBody = (<>
         <NoCommentEmptyStateMessageText>{t('conversations.emptyStateText.noComments.flavorTextLine1')}</NoCommentEmptyStateMessageText>
         <NoCommentEmptyStateMessageText>{t('conversations.emptyStateText.noComments.flavorTextLine2')}</NoCommentEmptyStateMessageText>
      </>);

      return (
         <EmptyState
            themeName={theme}
            title={searchText.length ? t('conversations.emptyStateText.noFilterResults') : t('conversations.emptyStateText.noComments.headerLine')}
            text={searchText.length ? undefined : noCommentBody}
         />
      );
   };

   const showEmptyState = !comments?.length || !comments.filter(c => !c.isFiltered).length;
   const emptyState: React.JSX.Element = (
      <EmptyStateWrapper theme={theme}>
         {searchText.length ? <SpotSearchGraphicGlyph /> : <SpotSpeechBubbleGraphicGlyph />}
         {getEmptyStateBody()}
      </EmptyStateWrapper>
   );

   const conversationOptions = [{value: '', label: t('conversations.emptyState.selectPrompt')}];
   if (!config.featureSwitches.UseSingleConversation) {
      conversations?.forEach(conversation => {
         conversationOptions.push({value: conversation.VideoReviewId, label: conversation.Title});
      });
   }

   const userAuthoredComment = !!comments?.some(c => c.authorId === config.user.TechSmithId || c.replies.some(r => r.authorId === config.user.TechSmithId));

   return (
      <>
         {conversationPaneOpen &&
            <ConversationPane id="conversation-pane" data-test-id="conversation-pane" theme={theme} isLti={config.environmentData.IsLti} tabIndex={-1} conversationPaneOpen={conversationPaneOpen} smartPlayerDisplayMode={smartPlayerDisplayMode} marketingPaneVisible={marketingPaneVisible}>
               <ConversationPaneHeaderWrapper theme={theme}>
                  <CommentSectionHeader theme={theme}>
                     <SectionHeading>{t('conversations.commentSectionLabel')}</SectionHeading>
                     <CommentHeaderRight>
                        {!!comments?.length && <CommentFilter searchText={searchText} setSearchText={setSearchText} executeSearch={onPerformCommentSearch} />}
                        {config.user.IsSignedIn && (config.featureSwitches.IsScreencast || config.watchConfig.isOwner || userAuthoredComment) && VideoReviewId && !searchText.length && <NotificationToggle userAuthoredComment={userAuthoredComment} />}
                        {!searchText.length &&
                           <ControlButton
                              glyph={<Glyphs.Close16x16 />}
                              buttonType={TscButtonType.tertiary}
                              title={conversationLoadingState.hasError ? t('general.close') : t('conversations.hideConversationPane')}
                              themeName={theme}
                              testId="hide-conversation-pane-button"
                              onClick={() => {
                                 conversationLoadingState.hasError && updateSelectedConversationState({VideoReviewId: '', Title: null, SelectedComment: null, ConversationHash: null});
                                 updateConversationsState({conversationPaneOpen: false});
                                 if (smartPlayerDisplayMode !== displayModes.Theater) {
                                    window.setTimeout(() => {
                                       document.getElementById('conversation-visibility-button')?.focus();
                                    }, Constants.getDomElementTimeout);
                                 }
                              }}
                           />}
                     </CommentHeaderRight>
                  </CommentSectionHeader>
                  {!config.featureSwitches.UseSingleConversation && !!conversations?.length && !config.environmentData.IsLti && config.watchConfig.isOwnerOrAdmin && <ConversationSelectWrapper theme={theme} borderNeeded={!!VideoReviewId} data-test-id={'conversation-select'} >
                     <SelectInput label={t('conversations.selectConversationLabel')} themeName={theme} value={VideoReviewId} onChange={updateConversation} options={conversationOptions} />
                  </ConversationSelectWrapper>}
                  {isSelfIdentified && !conversationLoadingState.hasError && <SelfIdentifiedUserWrapper theme={theme}>
                     {t('conversations.selfIdentified.commentingAs', {displayName: config.user.DisplayName})}
                     <ButtonStyledAsAnchor onClick={() => setShowSelfIdentifyAlertModal(true)} title={t('conversations.selfIdentified.changeUserToolTip')}> {t('conversations.selfIdentified.changeUser')}</ButtonStyledAsAnchor>
                  </SelfIdentifiedUserWrapper>}
                  {VideoReviewId && !conversationLoadingState.hasError && <CommentInput hasAnnotation={!!canvasRef?.getObjects().length} isCommentOwner={true} isReply={false} onSubmit={(commentText: string) => submitNewComment(commentText)} loadingState={addCommentLoadingState} />}
               </ConversationPaneHeaderWrapper>
               {conversationLoadingState.isWorking && !conversationLoadingState.hasError || (conversations == null && config.watchConfig.isOwnerOrAdmin) ? // eslint-disable-line eqeqeq
                  <SpinnerWrapper>
                     <Spinner themeName={theme} />
                  </SpinnerWrapper> :
                  <CommentSection className="comment-section" showEmptyState={showEmptyState}>
                     {showEmptyState ?
                        emptyState :
                        comments?.map((comment: IComment, index: number) => <Comment key={index} comment={comment} isReply={false} shouldShowUpdate={commentsToHighlight?.indexOf(comment.id) > -1} setCommentsToHighlight={setCommentsToHighlight} />)}
                     {<BottomSpacer />}
                  </CommentSection>}
            </ConversationPane>
         }
         {showCreateConversationModal && <CreateConversationModal onClose={() => setShowCreateConversationModal(false)} />}
         {showSelfIdentifyModal && <SelfIdentifyModal showTosMessage={true} showSignInMessage={true} isEmbedded={false} onClose={() => updateSelfIdentifiedUserProviderState({showSelfIdentifyModal: false})} />}
         {showSelfIdentifyAlertModal && <AlertModalPortal
            title={t('conversations.selfIdentified.changeUser')}
            confirmContent={t('conversations.selfIdentified.changeUser')}
            closeContent={t('general.cancel')}
            dismissContent={t('general.cancel')}
            onClose={() => setShowSelfIdentifyAlertModal(false)}
            onDismiss={() => setShowSelfIdentifyAlertModal(false)}
            onConfirm={clearSelfIdentifiedUser}
            dismissButtonType={TscButtonType.secondary}
            confirmButtonType={TscButtonType.destructive}>
            {t('conversations.selfIdentified.changeUserWarningText', {displayName: config.user.DisplayName})}
         </AlertModalPortal>}
      </>
   );
};

export interface IConversationProps {
   conversationLoadingState: IWorkerState;
   setConversationLoadingState: (updatedState: IWorkerState) => void;
   initialSelectedComment: IComment;
   smartPlayerDisplayMode: string;
   marketingPaneVisible: boolean;
}

export interface IStateMappedProps {
   canvasRef: any;
   comments: IComment[];
   VideoReviewId: string;
   conversations: IConversationData[];
   ConversationHash: string;
   conversationPaneOpen: boolean;
   theme: TscThemeNames;
   Hash: string;
   Title: string;
   showSelfIdentifyModal: boolean;
   IsReplacingMedia: boolean;
}

const mapStatesToProps = (
   {conversationLoadingState, setConversationLoadingState, initialSelectedComment, smartPlayerDisplayMode, marketingPaneVisible}: IConversationProps,
   {canvasRef}: Partial<IStateMappedProps>,
   {comments}: Partial<IStateMappedProps>,
   {VideoReviewId, ConversationHash}: Partial<IStateMappedProps>,
   {conversations, conversationPaneOpen}: Partial<IStateMappedProps>,
   {theme}: Partial<IStateMappedProps>,
   {Hash, Title}: Partial<IStateMappedProps>,
   {showSelfIdentifyModal}: Partial<IStateMappedProps>,
   {IsReplacingMedia}: Partial<IStateMappedProps>
): IStateMappedProps & IConversationProps => ({canvasRef, comments, VideoReviewId, Title, ConversationHash, conversations, conversationPaneOpen, conversationLoadingState, setConversationLoadingState, initialSelectedComment, smartPlayerDisplayMode, marketingPaneVisible, theme, Hash, showSelfIdentifyModal, IsReplacingMedia});

export default withMemoizedContexts(mapStatesToProps, canvasStore, commentsStore, selectedConversationStore, conversationsStore, themeStore, mediaStore, selfIdentifiedUserStore, mediaOperationsStore)(ConversationBase);
